import { DialogBox } from '../atomic/dialog';
import { TextField } from '../atomic/forms';
import { FoldersAPI, BreadcrumbResource } from '../../APIs/foldersApi';
import { Loading } from '../atomic/effects';
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Icons } from '../atomic/icons';
import { IconButton } from '../atomic/buttons';
import { HttpClient } from '../../utils/HttpUilts';
import { useToast } from '../atomic/toast';
import { CopyFoldersModel, FolderViewCheckModel } from '../FolderView';
import { FolderViewTypes } from './consts';
import { GroupSelector } from '../user/GroupSelecter';
import { useGet } from '../../hooks/useApi';
import { tenantApi } from '../../APIs/tenants';

export interface EditFolderDialogData {
    folderId: string;
    parentFolderId: string | null;
    name: string;
}
export interface EditFolderDialogProps extends EditFolderDialogData {
    onClose: (data: EditFolderDialogData | null | undefined) => void;
    setCopyFolders: React.Dispatch<
        React.SetStateAction<CopyFoldersModel | null>
    >;
}

export interface FolderCopyControllerProp {
    checkList: FolderViewCheckModel[];
    folderId: string;
    breadcrumbs: BreadcrumbResource[] | null;
    onDone: () => void;
    hasCollaborator: boolean;
    viewType: FolderViewTypes;
    copyFolders: CopyFoldersModel | null;
    setCopyFolders: (state: CopyFoldersModel | null) => void;
    isControl: boolean;
}

export const FolderCopyController = (prop: FolderCopyControllerProp) => {
    const { isAdmin, isUser } = useAuth();
    const [loading, setLoading] = useState<boolean>(false);
    const [showPasteConfirm, setShowPasteConfirm] = useState<boolean>(false);
    const {
        folderId,
        checkList,
        breadcrumbs,
        onDone,
        hasCollaborator,
        viewType,
        copyFolders,
        setCopyFolders,
        isControl,
    } = prop;
    const toast = useToast();

    const [groupIds, setGroupIds] = useState<Array<string>>([]);

    const folders: { id: string; name: string }[] = useMemo(
        () => checkList?.filter(({ isFile }) => !isFile) ?? [],
        [checkList]
    );

    const isPasetable: boolean = useMemo(() => {
        if (checkList.length) {
            return false;
        }
        if (!copyFolders?.folders?.length) {
            return false;
        }
        if (copyFolders?.parentFolderId == folderId) {
            return false;
        }
        if (
            breadcrumbs?.find(
                ({ folderId }) =>
                    !!copyFolders?.folders?.find(({ id }) => id == folderId)
            )
        ) {
            return false;
        }
        return true;
    }, [folders, breadcrumbs, copyFolders, folderId, checkList]);

    const isCopieable: boolean = useMemo(() => {
        return !!folders?.length;
    }, [folders]);

    const onCopyClick = useCallback(() => {
        setCopyFolders(
            folders?.length ? { parentFolderId: folderId, folders } : null
        );
        toast('フォルダをコピーしました');
    }, [folderId, folders, toast]);

    const { data: tenantStatus } = useGet(tenantApi.get.getTenantStatus);

    const onPasteClick = useCallback(() => {
        setShowPasteConfirm(true);
    }, []);

    const onPasteOk = useCallback(
        (dialog: DialogBox) => {
            if (!copyFolders?.folders?.length) {
                return false;
            }
            setLoading(true);

            Promise.all(
                copyFolders.folders.map((folder) =>
                    FoldersAPI.put(folder.id, {
                        parentFolderId:
                            folderId === undefined ? null : folderId,
                        folderName: folder.name,
                        groupIds,
                    })
                )
            )
                .then((resp) => {
                    setShowPasteConfirm(false);
                    setCopyFolders(null);
                    onDone();
                })
                .catch((err) => {
                    console.warn(err);
                    dialog.addError(
                        err?.response?.status == 404
                            ? '移動先のフォルダが削除されています'
                            : err,
                        'フォルダの更新に失敗しました'
                    );
                    setLoading(false);
                })
                .finally(() => {
                    setLoading(false);
                });
        },
        [folderId, copyFolders, groupIds]
    );

    const onPasteCancel = useCallback(() => {
        setShowPasteConfirm(false);
    }, []);

    // フォルダの移動はテナント管理者とユーザのみに許す
    if (!isAdmin() && !isUser()) {
        return <></>;
    }
    return (
        <>
            {viewType == FolderViewTypes.LIST && (
                <IconButton
                    icon={Icons.Copy}
                    onClick={onCopyClick}
                    disabled={!isCopieable}
                >
                    フォルダのコピー
                </IconButton>
            )}
            {isControl && (
                <IconButton
                    icon={Icons.Paste}
                    onClick={onPasteClick}
                    disabled={!isPasetable}
                >
                    フォルダの移動
                </IconButton>
            )}
            {showPasteConfirm && !!copyFolders?.folders?.length && (
                <DialogBox
                    onOK={onPasteOk}
                    disabled={
                        tenantStatus?.data.permission &&
                        isUser() &&
                        groupIds.length == 0 &&
                        folderId == null
                    }
                    onCancel={onPasteCancel}
                    className="paste-confirm-dialog"
                >
                    {hasCollaborator && (
                        <div className="collaborator-alert"></div>
                    )}
                    <p>
                        「{copyFolders.folders[0].name}」
                        {copyFolders.folders.length > 1 &&
                            `（他 ${copyFolders.folders.length - 1} フォルダ）`}
                        を移動しますか？
                    </p>
                    {isUser() && !!!folderId && (
                        <GroupSelector
                            setGroupIds={setGroupIds}
                            groupIds={groupIds}
                        />
                    )}
                </DialogBox>
            )}
            {loading && <Loading />}
        </>
    );
};

export const EditFolderDialog = ({
    folderId,
    parentFolderId,
    name,
    onClose,
    setCopyFolders,
}: EditFolderDialogProps) => {
    const [folderName, setFolderName] = React.useState<string>('');
    const [loading, setLoading] = React.useState<boolean>(false);

    const onOKHandle = React.useCallback(
        (dialog: DialogBox) => {
            setLoading(true);
            FoldersAPI.put(folderId, {
                parentFolderId: parentFolderId,
                folderName: folderName,
                groupIds: null,
            })
                .then((resp) => {
                    console.log(resp);
                    if (resp.success) {
                        onClose({
                            parentFolderId,
                            folderId,
                            name: folderName,
                        });
                        setCopyFolders(null);
                    } else {
                        dialog.addError(
                            resp.message || 'フォルダ情報の更新に失敗しました'
                        );
                    }
                })
                .catch((err) => {
                    console.warn(err);
                    dialog.addError(
                        err.message || 'フォルダ情報の更新に失敗しました'
                    );
                })
                .finally(() => {
                    setLoading(false);
                });
        },
        [folderName]
    );
    const onCancelHandle = React.useCallback(() => {
        onClose(null);
    }, []);
    React.useEffect(() => {
        setFolderName(name);
    }, [name]);

    return (
        <>
            <DialogBox
                title="フォルダ情報変更"
                onOK={onOKHandle}
                onCancel={onCancelHandle}
                className="edit-folder-dialog"
            >
                <TextField
                    label="フォルダ名"
                    value={folderName}
                    _onChange={setFolderName}
                    required={true}
                />
            </DialogBox>
            {loading && <Loading />}
        </>
    );
};
